import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Outlet } from "react-router-dom";
import API_CONFIG, { getApiUrl, apiRequest, clearClientSession } from '../../config/api';
import WhatsAppTemplatesPanel from '../WhatsAppTemplatesPanel';
import WhatsAppSettingsPanel from '../WhatsAppSettingsPanel';
import SupportInbox from '../SupportInbox';
import InstagramSettingsPanel from '../InstagramSettingsPanel';
import AccountMenu from '../AccountMenu';
import LegalContractSection from '../legal/LegalContractSection';
import AdminPanel from './AdminPanel';
import RegistroQueriesPanel from './RegistroQueriesPanel';
import ConfiguracionPanel from './ConfiguracionPanel';
import ChatsPanel from './ChatsPanel';
import MessagesPanel from './MessagesPanel';
import ContactosPanel from '../contactos/ContactosPanel';
import useChats from '../../hooks/useChats';
import useMessages from '../../hooks/useMessages';
import useStompConnection from '../../hooks/useStompConnection';
import useRutaConsola from '../../hooks/useRutaConsola';

// La URL de la entrada actual, con su query. El historial manual de este
// fichero la reescribe en su sitio en vez de forzar `/dashboard`, que pisaría
// la ruta. Lleva la query porque el OAuth de Instagram aterriza con ella.
const urlActual = () => window.location.pathname + window.location.search;
const aConjunto = (ids) => new Set((ids || [])
  .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
  .filter(id => !isNaN(id)));

export function primeraSeccionDisponible({ botId, botFeatures, atencionAccessBotIds, isAdmin }) {
  if (!botId) return null;
  if (botFeatures.chat.has(botId)) return 'conversaciones';
  if (atencionAccessBotIds.has(botId)) return 'atencion';
  if (botFeatures.registros.has(botId)) return 'registros';
  if (botFeatures.instagram.has(botId)) return 'comentarios-ig';
  if (botFeatures.whatsapp.has(botId)) return 'whatsapp';
  if (botFeatures.templates.has(botId) && botFeatures.whatsappConnected.has(botId)) {
    return 'plantillas';
  }
  if (botFeatures.contrato.has(botId)) return 'contrato';
  if (isAdmin) return 'configuracion';
  return null;
}

function seccionPermitida(seccion, { botId, botFeatures, atencionAccessBotIds, isAdmin }) {
  if (!botId) return false;
  if (seccion === 'conversaciones') return botFeatures.chat.has(botId);
  if (seccion === 'contactos') return botFeatures.chat.has(botId);
  if (seccion === 'atencion') return atencionAccessBotIds.has(botId);
  if (seccion === 'registros') return botFeatures.registros.has(botId);
  if (seccion === 'comentarios-ig') return botFeatures.instagram.has(botId);
  if (seccion === 'whatsapp') return botFeatures.whatsapp.has(botId);
  if (seccion === 'plantillas') {
    return botFeatures.templates.has(botId) && botFeatures.whatsappConnected.has(botId);
  }
  if (seccion === 'contrato') return botFeatures.contrato.has(botId);
  if (seccion === 'configuracion') return !!isAdmin;
  return false;
}

function Dashboard() {
  // La sección, el bot y la pestaña los manda la URL. `navigate` es el de
  // useRutaConsola, de identidad estable: useStompConnection depende de él.
  const { ruta, irA, navigate } = useRutaConsola();
  const rutaRef = useRef(ruta);
  const [sinBotsAccesibles, setSinBotsAccesibles] = useState(false);
  const activeTab = ruta?.admin ? 'administracion' : 'conversaciones';
  const selectedBotId = ruta?.botId ?? null;
  // Fuera de un bot (selector, administración) vale 'conversaciones', como el
  // estado inicial de antes; en /bots/:botId sin sección, null, para que el
  // efecto de sección permitida lleve a la primera disponible.
  const botSection = ruta?.seccion ?? (ruta?.botId ? null : 'conversaciones');
  const esperandoSeleccionDeBot = !!ruta?.selector && !sinBotsAccesibles;
  const [contactoInicial, setContactoInicial] = useState(null);
  const [fichaDesdeChat, setFichaDesdeChat] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const selectedBotIdRef = useRef(null);
  const [accessibleBots, setAccessibleBots] = useState([]); // Bots accesibles del usuario
  // Permisos por funcionalidad del usuario, en un solo GET. Sustituye al antiguo
  // /instagram/comments/access, que solo sabía de comentarios y además filtraba por
  // "el bot tiene canal IG": eso escondía el conector justo cuando hacía falta para
  // conectarlo. Failsafe-cerrado: si el endpoint no responde, no se pinta nada.
  const [botFeatures, setBotFeatures] = useState(() => ({
    whatsapp: new Set(),
    instagram: new Set(),
    templates: new Set(),
    registros: new Set(),
    chat: new Set(),
    contrato: new Set(),
    whatsappConnected: new Set(),
  }));
  // Bots donde el usuario puede ejercer de agente de "Atención al cliente".
  const [atencionAccessBotIds, setAtencionAccessBotIds] = useState(() => new Set());
  // Hasta que lleguen los dos, los efectos que corrigen una sección no
  // permitida no actúan: con la sección en la URL, un administrador que
  // recarga /bots/1/templates saltaría a Configuración antes de saber si el
  // bot tiene plantillas. Fallar también cuenta como llegado (failsafe).
  const [featuresCargadas, setFeaturesCargadas] = useState(false);
  const [atencionCargada, setAtencionCargada] = useState(false);
  const permisosCargados = featuresCargadas && atencionCargada;

  // Mapa botId -> timezone para poder formatear fechas en la zona horaria correcta del bot
  // (por ejemplo en la lista de chats). Se recalcula sólo cuando cambian los bots.
  const botTimezonesMap = useMemo(() => {
    const map = {};
    for (const b of accessibleBots) {
      if (b && b.botId != null) {
        map[String(b.botId)] = b.timezone || 'Europe/Madrid';
      }
    }
    return map;
  }, [accessibleBots]);

  // Cargar bots accesibles del usuario
  const loadAccessibleBots = useCallback(async () => {
    try {
      const response = await apiRequest(getApiUrl('/bots/accessible'));
      if (response.ok) {
        const bots = await response.json();
        setAccessibleBots(bots);
        // El ref, no el estado: si el usuario elige un bot mientras
        // esta petición sigue en vuelo, `selectedBotId` de este
        // cierre sigue nulo y no hay que pisarle la elección.
        // Solo se entra solo cuando hay exactamente un bot, y solo desde el
        // selector: desde /admin, con un único bot recién creado, no hay que
        // sacar al usuario de Administración.
        if (!selectedBotIdRef.current) {
          setSinBotsAccesibles(bots.length === 0);
          if (bots.length === 1 && rutaRef.current?.selector) {
            selectedBotIdRef.current = bots[0].botId;
            irA({ botId: bots[0].botId }, { replace: true });
          }
        }
      }
    } catch (error) {
      console.error('Error loading accessible bots:', error);
    }
  }, [selectedBotId, irA]);
  
  // Cargar bots al montar el componente
  useEffect(() => {
    loadAccessibleBots();
  }, [loadAccessibleBots]);

  const loadBotFeatures = useCallback(async () => {
    try {
      const response = await apiRequest(getApiUrl(API_CONFIG.BOTS.FEATURES));
      if (!response.ok) return;
      const data = await response.json();
      setBotFeatures({
        whatsapp: aConjunto(data?.whatsappBotIds),
        instagram: aConjunto(data?.instagramBotIds),
        templates: aConjunto(data?.templateBotIds),
        registros: aConjunto(data?.registroBotIds),
        chat: aConjunto(data?.chatBotIds),
        contrato: aConjunto(data?.contratoBotIds),
        whatsappConnected: aConjunto(data?.whatsappConnectedBotIds),
      });
    } catch (_) {
      // Sin acceso o sin red: pestañas ocultas.
    } finally {
      setFeaturesCargadas(true);
    }
  }, []);

  useEffect(() => {
    loadBotFeatures();
  }, [loadBotFeatures]);

  // Cargar los bots con permiso de "Atención al cliente". El apartado y el botón
  // "Capturar" solo se pintan para bots que aparezcan en este Set (failsafe-cerrado).
  const loadAtencionAccessBotIds = useCallback(async () => {
    try {
      const response = await apiRequest(getApiUrl('/support/access'));
      if (!response.ok) return;
      const data = await response.json();
      const ids = (data?.botIds || [])
        .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
        .filter(id => !isNaN(id));
      setAtencionAccessBotIds(new Set(ids));
    } catch (_) {
      // Sin acceso o sin red: apartado oculto.
    } finally {
      setAtencionCargada(true);
    }
  }, []);

  useEffect(() => {
    loadAtencionAccessBotIds();
  }, [loadAtencionAccessBotIds]);
  
  // Lógica de la lista de conversaciones (estado, filtros, paginación, carga y
  // recarga). El hook es dueño de `chats`; el contenedor le baja lo compartido
  // (bot, ref del bot, pestaña, chat seleccionado) y recibe `setChats`, `reload`
  // y `scheduleReload` para que el envío de mensajes, el marcado de leídos y los
  // callbacks del WebSocket sigan escribiendo la lista igual que antes.
  const {
    chats,
    setChats,
    chatFilters,
    setChatFilters,
    appliedChatFilters,
    availableChannels,
    chatsPagination,
    loadMoreChats,
    handleSearchChats,
    handleClearChatFilters,
    handleRemoveChatFilter,
    reload: reloadChatsFromBackend,
    scheduleReload: scheduleReloadChats,
  } = useChats({
    selectedBotId,
    selectedBotIdRef,
    activeTab,
    selectedChat,
    setSelectedChat,
    puedeVerChatEnVivo: !!(selectedBotId && botFeatures.chat.has(selectedBotId)),
  });

  useEffect(() => {
    selectedBotIdRef.current = selectedBotId;
  }, [selectedBotId]);

  useEffect(() => {
    rutaRef.current = ruta;
  }, [ruta]);

  // Verificar si el usuario es admin (con estado para actualizar dinámicamente)
  // Inicializar con el valor de localStorage para evitar delay en el render
  const [isAdmin, setIsAdmin] = useState(() => {
    const userRoles = JSON.parse(localStorage.getItem("userRoles") || "[]");
    return userRoles.includes("ROLE_ADMIN");
  });
  
  // Cargar información del usuario al montar el componente
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const response = await apiRequest(getApiUrl('/auth/user'));
        if (response.ok) {
          const userData = await response.json();
          // El rol puede venir como string "ROLE_ADMIN" o como objeto
          const userRole = typeof userData.role === 'string' 
            ? userData.role 
            : (userData.role?.roleName || userData.role);
          const admin = userRole === 'ROLE_ADMIN';
          setIsAdmin(admin);
          // Actualizar localStorage también
          const roles = admin ? ['ROLE_ADMIN'] : ['ROLE_USER'];
          localStorage.setItem("userRoles", JSON.stringify(roles));
        } else {
          // Si falla la petición (400, 401, etc.), usar el valor de localStorage como fallback
          const userRoles = JSON.parse(localStorage.getItem("userRoles") || "[]");
          const admin = userRoles.includes("ROLE_ADMIN");
          setIsAdmin(admin);
        }
      } catch (error) {
        // Si falla, usar el valor de localStorage como fallback
        const userRoles = JSON.parse(localStorage.getItem("userRoles") || "[]");
        const admin = userRoles.includes("ROLE_ADMIN");
        setIsAdmin(admin);
      }
    };
    checkUserRole();
  }, []);
  
  // Estados para panel de administración
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminBots, setAdminBots] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');

  // Mensajes específicos por sección de administración
  const [configTab, setConfigTab] = useState('ajustes');
 
  // Estados para registro queries (pestaña Registros del bot)
  const [registroQueries, setRegistroQueries] = useState([]);
  const [selectedRegistroQuery, setSelectedRegistroQuery] = useState(null);
  const [registroQueryResult, setRegistroQueryResult] = useState(null);
  const [registroQueryLoading, setRegistroQueryLoading] = useState(false);

  const selectedChatRef = useRef(null);
  const selectedRegistroQueryRef = useRef(null);
  const botSectionRef = useRef('conversaciones');
  const permisosSeccionRef = useRef({
    botId: null,
    botFeatures: { chat: new Set(), registros: new Set(), whatsapp: new Set(), instagram: new Set(), templates: new Set(), contrato: new Set(), whatsappConnected: new Set() },
    atencionAccessBotIds: new Set(),
    isAdmin: false,
  });
  const historyBufferInitialized = useRef(false);

  // Establecer título de la página
  useEffect(() => {
    document.title = "Botomatic - Dashboard";
  }, []);
  
  // Establecer estado inicial del historial al montar el componente
  useEffect(() => {
    // Solo establecer el estado inicial si no existe o no es dashboard
    if (!window.history.state || window.history.state.view !== 'dashboard') {
      window.history.replaceState({ view: 'dashboard' }, '', urlActual());
    }
  }, []);
  
  
    // ===== FUNCIONES DE ADMINISTRACIÓN =====

    // Cargar usuarios y bots cuando se abre la pestaña de administración
    const loadAdminData = useCallback(async () => {
      if (!isAdmin) return;
      
      setAdminLoading(true);
      setAdminError('');
      
      try {
        // Cargar usuarios
        const usersResponse = await apiRequest(getApiUrl('/admin/getusers'));
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          setAdminUsers(usersData);
        }
        
        // Cargar bots
        const botsResponse = await apiRequest(getApiUrl('/admin/bots'));
        if (botsResponse.ok) {
          const botsData = await botsResponse.json();
          setAdminBots(botsData);
        }
      } catch (error) {
        console.error('Error loading admin data:', error);
        setAdminError('Error al cargar datos');
      } finally {
        setAdminLoading(false);
      }
    }, [isAdmin]);
    
    // ===== FUNCIONES DE REGISTRO QUERIES =====

    const loadRegistroQueries = useCallback(async (botId) => {
      if (!botId) return;
      try {
        const response = await apiRequest(getApiUrl(`/bots/${botId}/registro-queries`));
        if (response.ok) {
          const data = await response.json();
          setRegistroQueries(data);
        } else {
          setRegistroQueries([]);
        }
      } catch (error) {
        console.error('Error loading registro queries:', error);
        setRegistroQueries([]);
      }
    }, []);

    const executeRegistroQuery = useCallback(async (botId, queryId) => {
      setRegistroQueryLoading(true);
      setRegistroQueryResult(null);
      try {
        const response = await apiRequest(getApiUrl(`/bots/${botId}/registro-queries/${queryId}/execute`));
        if (response.ok) {
          const data = await response.json();
          setRegistroQueryResult(data);
        } else {
          const err = await response.json();
          setRegistroQueryResult({ error: err.error || 'Error al ejecutar la consulta' });
        }
      } catch (error) {
        console.error('Error executing registro query:', error);
        setRegistroQueryResult({ error: 'Error de conexión' });
      } finally {
        setRegistroQueryLoading(false);
      }
    }, []);

    // Hasta las rutas, las consultas solo se pedían al pulsar el botón de la
    // sección; con un enlace directo a Registros nadie las pediría.
    useEffect(() => {
      if (botSection === 'registros' && selectedBotId) {
        loadRegistroQueries(selectedBotId);
      }
    }, [botSection, selectedBotId, loadRegistroQueries]);

    const handleSelectRegistroQuery = useCallback((query) => {
      setSelectedRegistroQuery(query);
      setRegistroQueryResult(null);
      if (selectedBotId && query) {
        executeRegistroQuery(selectedBotId, query.id);
      }
      if (query) {
        window.history.pushState({ view: 'registro', queryId: query.id }, '', urlActual());
      }
    }, [selectedBotId, executeRegistroQuery]);

    const [appliedTaskTimezone, setAppliedTaskTimezone] = useState(null);

    // Cargar datos cuando se selecciona la pestaña de administración
    useEffect(() => {
      if (activeTab === 'administracion' && isAdmin) {
        loadAdminData();
      }
    }, [activeTab, isAdmin, loadAdminData]);

  const handleLogout = async () => {
      try {
        // Llamar al endpoint de logout para limpiar la cookie del servidor
        await apiRequest(getApiUrl("/auth/logout"), {
          method: "POST",
        });
      } catch (error) {
        console.error("Error during logout:", error);
      }
      
      clearClientSession();
      navigate("/");
    };

  // Función para marcar mensajes como leídos
  const markChatAsRead = async (chatId, platform, botId = null) => {
    try {
      // ✅ CRÍTICO: Incluir botId como query parameter para identificar el bot correcto
      let endpoint = platform === 'telegram' 
        ? getApiUrl(`/telegram/chats/${chatId}/mark-read`)
        : platform === 'instagram'
        ? getApiUrl(`/instagram/chats/${chatId}/mark-read`)
        : getApiUrl(`/whatsapp/chats/${chatId}/mark-read`);
      
      // Agregar botId como query parameter si está disponible
      if (botId) {
        const separator = endpoint.includes('?') ? '&' : '?';
        endpoint += `${separator}botId=${botId}`;
      }
      
      console.log('📤 Marcando mensajes como leídos:', endpoint, botId ? `(botId: ${botId})` : '');
      
      const response = await apiRequest(endpoint, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Mensajes marcados como leídos:', result);
        
        // ✅ CRÍTICO: Actualizar el chat en la lista verificando tanto chatId como botId
        setChats(prevChats => 
          prevChats.map(c => {
            // Verificar que coincida tanto chatId como botId
            const chatIdMatches = String(c.chatId) === String(chatId);
            const botIdMatches = !botId || !c.botId || Number(c.botId) === Number(botId);
            
            if (chatIdMatches && botIdMatches) {
              return { ...c, unread: 0, avatar: c.platform === 'telegram' ? 'TG' : c.platform === 'instagram' ? 'IG' : 'WA' };
            }
            return c;
          })
        );
        return true;
      } else {
        console.error('❌ Error marcando mensajes como leídos:', response.statusText);
        return false;
      }
    } catch (error) {
      console.error('❌ Error marcando mensajes como leídos:', error);
      return false;
    }
  };

  // Conexión WebSocket/STOMP: crea el cliente, se suscribe a los topics por
  // tenant y escribe `chats`/`selectedChat` cuando llegan eventos. Recibe las
  // refs y los setters que viven en el contenedor (o en useChats) para no
  // recrear el cliente en cada render.
  useStompConnection({
    selectedBotIdRef,
    selectedChatRef,
    setSelectedChat,
    setChats,
    accessibleBots,
    scheduleReloadChats,
    reloadChatsFromBackend,
    markChatAsRead,
    navigate,
  });

  // Lógica de los mensajes del chat abierto: texto del input, estado de atención
  // (capturar/cerrar), paginación de mensajes, envío y apertura de un chat. El hook
  // es dueño de esos estados; el contenedor le baja `selectedChat`/`setSelectedChat`
  // /`selectedChatRef` (que comparten chats, historial, CSS y WebSocket),
  // `selectedBotId`, `setChats` y `reloadChatsFromBackend` (de useChats) y
  // `markChatAsRead` (que también usa el callback del WebSocket, por eso se queda en
  // el contenedor). Devuelve `handleChatSelect` (puente lista→panel) y todo lo que
  // pinta MessagesPanel.
  const {
    message,
    setMessage,
    handleSendMessage,
    handleKeyPress,
    messageInputRef,
    messagesContainerRef,
    messagesPagination,
    loadMoreMessages,
    supportStatus,
    supportActionLoading,
    handleCaptureConversation,
    handleCloseConversation,
    handleChatSelect,
    ventanaCerrada,
    sendErrorText,
    alCargarMedia,
    handleSendMedia,
    enviandoMedia,
  } = useMessages({
    selectedChat,
    setSelectedChat,
    selectedChatRef,
    selectedBotId,
    setChats,
    reloadChatsFromBackend,
    markChatAsRead,
  });

  // Efecto para mantener selectedChatRef actualizado
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    selectedRegistroQueryRef.current = selectedRegistroQuery;
  }, [selectedRegistroQuery]);

  useEffect(() => {
    botSectionRef.current = botSection;
  }, [botSection]);

  useEffect(() => {
    permisosSeccionRef.current = {
      botId: selectedBotId,
      botFeatures,
      atencionAccessBotIds,
      isAdmin,
    };
  }, [selectedBotId, botFeatures, atencionAccessBotIds, isAdmin]);

  // Manejo unificado del historial como aplicación nativa (optimizado)
  useEffect(() => {
    const handlePopState = (event) => {
      if (selectedChatRef.current) {
        setSelectedChat(null);
        window.history.replaceState({ view: 'dashboard', salida: 'trampa' }, '', urlActual());
      } else if (selectedRegistroQueryRef.current) {
        setSelectedRegistroQuery(null);
        setRegistroQueryResult(null);
        window.history.replaceState({ view: 'dashboard', salida: 'trampa' }, '', urlActual());
      } else if (botSectionRef.current === 'atencion'
          && document.querySelector('.support-inbox.has-selection')) {
        // Atrás con conversación de Atención abierta: cierra el chat (slide a lista),
        // sin salir de la sección. SupportInbox escucha el evento.
        window.dispatchEvent(new Event('support-inbox-back'));
        window.history.replaceState({ view: 'atencion' }, '', urlActual());
      } else if (botSectionRef.current === 'atencion'
          || botSectionRef.current === 'comentarios-ig'
          || botSectionRef.current === 'plantillas'
          || botSectionRef.current === 'whatsapp'
          || botSectionRef.current === 'registros'
          || botSectionRef.current === 'contactos') {
        // Atrás dentro de una sección: a Chat si hay permiso. Si Chat no
        // existe, o pedimos salir (ya estamos en la primera disponible)
        // o saltamos a esa primera sin empujar otra entrada de historial.
        // Con rutas, la sección se cambia con irA y el replaceState de
        // después, que ya ve la URL nueva, solo deja la marca de estado.
        const primera = primeraSeccionDisponible(permisosSeccionRef.current);
        const actual = botSectionRef.current;
        const { botId } = permisosSeccionRef.current;
        if (primera === 'conversaciones') {
          irA({ botId, seccion: 'conversaciones' }, { replace: true });
          window.history.replaceState({ view: 'dashboard', salida: 'trampa' }, '', urlActual());
        } else if (primera == null || primera === actual) {
          window.dispatchEvent(new Event('dashboard-quiere-salir'));
        } else {
          const view = primera === 'atencion' ? 'atencion'
            : primera === 'comentarios-ig' ? 'instagram'
            : 'dashboard';
          irA({ botId, seccion: primera }, { replace: true });
          window.history.replaceState(
            { view, salida: view === 'dashboard' ? 'trampa' : undefined },
            '',
            urlActual(),
          );
        }
      } else {
        window.dispatchEvent(new Event('dashboard-quiere-salir'));
      }
    };

    // Agregar listener para el evento popstate
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [irA]); // irA es estable: solo se monta una vez
  
  // Inicializar buffer del historial solo una vez al montar
  useEffect(() => {
    if (!historyBufferInitialized.current) {
      const currentState = window.history.state;
      if (!currentState || currentState.view !== 'dashboard') {
        window.history.replaceState({ view: 'dashboard' }, '', urlActual());
      }
      // Agregar un estado buffer inicial solo si es necesario
      if (window.history.length <= 1) {
        window.history.pushState({ view: 'dashboard' }, '', urlActual());
      }
      historyBufferInitialized.current = true;
    }
  }, []); // Solo se ejecuta una vez al montar

  // Prevenir scroll del body cuando hay un chat seleccionado en móvil. Depende de
  // si hay chat abierto, no del objeto: cada tick o evento de archivo crea un
  // selectedChat nuevo y antes quitaba y volvía a poner la clase.
  const hayChatAbierto = !!selectedChat;
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile && hayChatAbierto && activeTab === 'conversaciones') {
      // Agregar clase al body para prevenir scroll
      document.body.classList.add('chat-selected');
      
      return () => {
        // Remover clase cuando se deselecciona el chat
        document.body.classList.remove('chat-selected');
      };
    } else {
      // Remover clase si no hay chat seleccionado
      document.body.classList.remove('chat-selected');
    }
  }, [hayChatAbierto, activeTab]);

  // ===== Resultado del flujo OAuth de Instagram =====
  // El backend redirige a /dashboard?ig_oauth=success|error con datos en query.
  // Aquí detectamos esos parámetros, mostramos un modal y limpiamos la URL.
  const [instagramOAuthResult, setInstagramOAuthResult] = useState(null);
  // Tras OAuth, queremos posicionar al usuario en la pestaña Instagram del
  // bot que acaba de conectar. Guardamos aquí el botId leído del redirect y
  // lo aplicamos cuando `accessibleBots` y `botFeatures` ya estén
  // cargados (de lo contrario el "guard" de elegibilidad lo devolvería a la
  // sección de conversaciones).
  const [pendingIgNavigationBotId, setPendingIgNavigationBotId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('ig_oauth');
    if (!status) return;

    // ─── Modo popup: cerrar y notificar a la pestaña padre ────────────
    //
    // El backend redirige tras el OAuth a /dashboard?ig_oauth=…, así
    // que la pestaña popup carga el Dashboard completo. Antes esto
    // dejaba al usuario varado en el dashboard del popup y la pestaña
    // padre no se enteraba de nada hasta refrescar.
    //
    // Detectamos "soy un popup OAuth" mirando `window.name`:
    // `InstagramSettingsPanel.handleConnect` abre la pestaña con
    // `window.open('about:blank', 'botomatic_ig_oauth_popup')`, y ese
    // nombre PERSISTE a través de las navegaciones cross-origin
    // (instagram.com → backend → dashboard). NO podemos usar
    // `window.opener` aquí porque el opener se anula a propósito
    // como mitigación anti-tabnabbing al abrir Instagram.
    //
    // Si somos popup, publicamos el resultado al BroadcastChannel
    // (lo escucha la pestaña padre) e intentamos cerrar la pestaña.
    // BroadcastChannel funciona entre pestañas del MISMO origen sin
    // necesidad de relación opener-child, así que es robusto frente
    // a la anulación del opener. Si `window.close` falla (la pestaña
    // padre puede cerrarnos manualmente porque conserva la
    // referencia a `newTab`), el padre hará el cleanup.
    //
    // Si NO somos popup (acceso directo, el usuario pegó la URL en
    // el navegador), seguimos con el comportamiento clásico:
    // modal + navegar al bot recién conectado.
    const isOAuthPopup = (() => {
      try {
        return window.name === 'botomatic_ig_oauth_popup';
      } catch (_) {
        return false;
      }
    })();

    if (isOAuthPopup) {
      const payload = status === 'success'
        ? {
            type: 'instagram-oauth-result',
            status: 'success',
            botId: params.get('botId') ? Number(params.get('botId')) : null,
            botChannelId: params.get('botChannelId') ? Number(params.get('botChannelId')) : null,
            username: params.get('username') || null,
          }
        : {
            type: 'instagram-oauth-result',
            status: 'error',
            code: params.get('code') || 'unknown_error',
            message: params.get('message') || '',
          };

      let channel = null;
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          channel = new BroadcastChannel('botomatic-ig-oauth');
          channel.postMessage(payload);
        } catch (_) {
          // Sin BroadcastChannel disponible: la pestaña padre no se
          // enterará automáticamente, pero el flujo sigue siendo
          // correcto en backend. El usuario verá el panel actualizado
          // la próxima vez que cargue /status (o al recargar el
          // dashboard). No bloqueante.
        }
      }

      // Limpiamos `window.name` para que si por algún motivo esta
      // pestaña no se cierra y el usuario navega manualmente, no
      // siga marcada como popup OAuth en futuros refrescos.
      try { window.name = ''; } catch (_) {}

      // Limpiamos la URL antes de cerrar: si window.close se bloquea
      // y el usuario se queda mirando esta pestaña, al menos no
      // tendrá los parámetros sensibles a la vista ni se re-disparará
      // la lógica al recargar.
      try {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (_) {}

      // Pequeño delay para que el BroadcastChannel termine de propagar
      // antes del close (algunos navegadores invalidan canales en
      // pestañas cerradas y se pierden mensajes en vuelo).
      setTimeout(() => {
        try { if (channel) channel.close(); } catch (_) {}
        try {
          window.close();
        } catch (_) {
          // Si el navegador bloquea window.close (típico cuando la
          // pestaña no la abrió un script), la pestaña padre la
          // cerrará al recibir el BroadcastChannel.
        }
      }, 150);

      // Importante: NO seteamos `instagramOAuthResult` ni
      // `pendingIgNavigationBotId` aquí. Eso es responsabilidad de la
      // pestaña padre, que ya está siendo notificada.
      return;
    }

    // ─── Modo no-popup: comportamiento clásico ─────────────────────────
    if (status === 'success') {
      setInstagramOAuthResult({
        status: 'success',
        botChannelId: params.get('botChannelId') || '',
        botId: params.get('botId') || '',
        username: params.get('username') || '',
        igUserId: params.get('igUserId') || ''
      });
      const botIdNum = Number(params.get('botId'));
      if (Number.isFinite(botIdNum) && botIdNum > 0) {
        setPendingIgNavigationBotId(botIdNum);
      }
    } else if (status === 'error') {
      setInstagramOAuthResult({
        status: 'error',
        code: params.get('code') || 'unknown',
        message: params.get('message') || 'Se ha producido un error al conectar Instagram'
      });
    }

    // Limpiar la URL para que recargar la página no re-dispare el modal.
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }, []);

  // Tras conectar con éxito, recargar bots y permisos de comentarios para que
  // el bot recién conectado figure como elegible y la pestaña Instagram pueda
  // renderizar el estado CONNECTED inmediatamente.
  useEffect(() => {
    if (instagramOAuthResult && instagramOAuthResult.status === 'success') {
      loadAccessibleBots();
      loadBotFeatures();
    }
  }, [instagramOAuthResult, loadAccessibleBots, loadBotFeatures]);

  // Aplica la navegación post-OAuth: cuando los bots y los permisos están
  // cargados y el bot recién conectado es elegible, lo seleccionamos y
  // saltamos a la pestaña Instagram. Hay un pequeño desfase entre /provision
  // y /comments/access (la query del backend filtra por `bot.hasChannel`),
  // por eso esperamos a que el bot aparezca antes de aplicar el cambio.
  useEffect(() => {
    if (pendingIgNavigationBotId == null) return;
    if (accessibleBots.length === 0) return;
    const bot = accessibleBots.find(b => b.botId === pendingIgNavigationBotId);
    if (!bot) return;
    selectedBotIdRef.current = pendingIgNavigationBotId;
    if (botFeatures.instagram.has(pendingIgNavigationBotId)) {
      irA({ botId: pendingIgNavigationBotId, seccion: 'comentarios-ig' }, { replace: true });
      setPendingIgNavigationBotId(null);
    } else if (selectedBotId !== pendingIgNavigationBotId) {
      irA({ botId: pendingIgNavigationBotId }, { replace: true });
    }
  }, [pendingIgNavigationBotId, accessibleBots, botFeatures, selectedBotId, irA]);

  // Si el usuario tenía abierta la pestaña "Instagram" y cambia a un bot
  // que ya no la soporta (sin canal IG o sin permiso), volvemos a la sección
  // por defecto. Evita un estado limbo en el que el panel principal trataría
  // de pintar comentarios de un bot inválido. Excluimos el caso en el que
  // hay una navegación post-OAuth pendiente para que no se cancele a sí
  // misma mientras los datos terminan de cargarse.
  useEffect(() => {
    if (botSection !== 'comentarios-ig') return;
    if (!selectedBotId) return;
    if (!permisosCargados) return;
    if (pendingIgNavigationBotId != null) return;
    if (!botFeatures.instagram.has(selectedBotId)) {
      const siguiente = primeraSeccionDisponible({
        botId: selectedBotId, botFeatures, atencionAccessBotIds, isAdmin,
      });
      if (siguiente != null) irA({ botId: selectedBotId, seccion: siguiente }, { replace: true });
    }
  }, [selectedBotId, botSection, botFeatures, pendingIgNavigationBotId, permisosCargados]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si el bot seleccionado deja de tener permiso de WhatsApp, salimos del conector.
  useEffect(() => {
    if (botSection !== 'whatsapp') return;
    if (!selectedBotId) return;
    if (!permisosCargados) return;
    if (!botFeatures.whatsapp.has(selectedBotId)) {
      const siguiente = primeraSeccionDisponible({
        botId: selectedBotId, botFeatures, atencionAccessBotIds, isAdmin,
      });
      if (siguiente != null) irA({ botId: selectedBotId, seccion: siguiente }, { replace: true });
    }
  }, [selectedBotId, botSection, botFeatures, permisosCargados]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si el bot seleccionado pierde el permiso o deja de tener número conectado,
  // salimos de Plantillas para no dejar el panel pidiendo plantillas de la nada.
  useEffect(() => {
    if (botSection !== 'plantillas') return;
    if (!selectedBotId) return;
    if (!permisosCargados) return;
    const sigueValiendo = botFeatures.templates.has(selectedBotId)
      && botFeatures.whatsappConnected.has(selectedBotId);
    if (!sigueValiendo) {
      const siguiente = primeraSeccionDisponible({
        botId: selectedBotId, botFeatures, atencionAccessBotIds, isAdmin,
      });
      if (siguiente != null) irA({ botId: selectedBotId, seccion: siguiente }, { replace: true });
    }
  }, [selectedBotId, botSection, botFeatures, permisosCargados]); // eslint-disable-line react-hooks/exhaustive-deps

  // Análogo para "Atención al cliente": si el bot seleccionado deja de tener
  // permiso de agente, salimos de la sección.
  useEffect(() => {
    if (botSection !== 'atencion') return;
    if (!selectedBotId) return;
    if (!permisosCargados) return;
    if (!atencionAccessBotIds.has(selectedBotId)) {
      const siguiente = primeraSeccionDisponible({
        botId: selectedBotId, botFeatures, atencionAccessBotIds, isAdmin,
      });
      if (siguiente != null) irA({ botId: selectedBotId, seccion: siguiente }, { replace: true });
    }
  }, [selectedBotId, botSection, atencionAccessBotIds, permisosCargados]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si la sección actual no está permitida (cambio de bot o de features),
  // salta a la primera disponible. Si no hay ninguna, deja la barra vacía.
  useEffect(() => {
    if (!selectedBotId) return;
    if (!permisosCargados) return;
    if (pendingIgNavigationBotId != null && botSection === 'comentarios-ig') return;
    const args = { botId: selectedBotId, botFeatures, atencionAccessBotIds, isAdmin };
    if (seccionPermitida(botSection, args)) return;
    const siguiente = primeraSeccionDisponible(args);
    if (siguiente != null) irA({ botId: selectedBotId, seccion: siguiente }, { replace: true });
  }, [selectedBotId, botSection, botFeatures, atencionAccessBotIds, isAdmin, pendingIgNavigationBotId, permisosCargados, irA]);

  // Pushear entrada al historial al entrar en Atención (atrás del SO → conversaciones).
  useEffect(() => {
    if (botSection !== 'atencion') return;
    const currentState = window.history.state;
    if (!currentState || (currentState.view !== 'atencion' && currentState.view !== 'support')) {
      window.history.pushState({ view: 'atencion' }, '', urlActual());
    }
  }, [botSection]);

  // Pushear una entrada al historial al entrar en la sección Instagram para
  // que el botón "atrás" del SO (flecha de Android, swipe en iOS) vuelva al
  // dashboard en lugar de salirse a la pantalla de login. La salida la
  // gestiona handlePopState — aquí solo "marcamos" la entrada.
  useEffect(() => {
    if (botSection !== 'comentarios-ig') return;
    const currentState = window.history.state;
    if (!currentState || currentState.view !== 'instagram') {
      window.history.pushState({ view: 'instagram' }, '', urlActual());
    }
  }, [botSection]);

  return (
    <div className={`dashboard ${(selectedChat && activeTab === 'conversaciones' && botSection === 'conversaciones') || (selectedRegistroQuery && botSection === 'registros') ? 'chat-selected' : ''}`}>
      {/* La guarda de rol de /admin, que App.js declara como ruta hija. */}
      <Outlet />
      {/* Header */}
      <header className={`dashboard-header ${(selectedChat && activeTab === 'conversaciones' && botSection === 'conversaciones') || (selectedRegistroQuery && botSection === 'registros') ? 'hidden-on-mobile' : ''}`}>
        <div className="header-left">
          <h1>Dashboard</h1>
          {activeTab !== 'administracion' && (accessibleBots.length > 1 || esperandoSeleccionDeBot) && (
            <select
              className={`bot-dropdown-tab ${activeTab === 'conversaciones' && !esperandoSeleccionDeBot ? 'active' : ''}`}
              aria-label="Bots"
              value={esperandoSeleccionDeBot ? '' : (selectedBotId || '')}
              onChange={(e) => {
                const valor = e.target.value;
                if (valor === '') return;
                const newBotId = Number(valor);
                // El ref antes del irA, a propósito: convertirlo en efecto
                // vuelve a mezclar chats de dos bots. Elegir bot sustituye la
                // entrada: hoy no crea historial y el atrás manual lo
                // trataría como salida. Registros del bot nuevo los recarga
                // el efecto de la sección.
                selectedBotIdRef.current = newBotId;
                setSelectedRegistroQuery(null);
                setContactoInicial(null);
                setFichaDesdeChat(false);
                setRegistroQueryResult(null);
                setRegistroQueries([]);
                irA({ botId: newBotId, seccion: botSection }, { replace: true });
              }}
            >
              {esperandoSeleccionDeBot && (
                <option value="">Selecciona un bot</option>
              )}
              {accessibleBots.map(bot => (
                <option key={bot.botId} value={bot.botId}>
                  {bot.botName}
                </option>
              ))}
            </select>
          )}
        </div>
        {isAdmin && (
          <div className="header-center">
            <div className="tabs-container">
              {activeTab === 'administracion' && (
                <button
                  className="tab-btn"
                  onClick={() => {
                    setSelectedChat(null);
                    setSelectedRegistroQuery(null);
                    setRegistroQueryResult(null);
                    irA({ selector: true }, { replace: true });
                  }}
                >
                  Bots
                </button>
              )}
              <button
                className={`tab-btn ${activeTab === 'administracion' ? 'active' : ''}`}
                onClick={() => irA({ admin: true }, { replace: true })}
              >
                Administración
              </button>
            </div>
          </div>
        )}
        <div className="header-right">
          <AccountMenu onLogout={handleLogout} />
        </div>
      </header>

      {/* Contenido según pestaña activa */}
      {activeTab === 'conversaciones' && esperandoSeleccionDeBot && accessibleBots.length > 0 ? (
      <div className="admin-environment bot-selection-screen">
        <div className="admin-bots-presentacion">
          <h2>Selecciona un bot</h2>
          <p>Elige un bot en el desplegable de la izquierda para continuar.</p>
        </div>
      </div>
      ) : activeTab === 'conversaciones' && !esperandoSeleccionDeBot ? (
      /* Workspace principal: barra lateral vertical + área de contenido */
      <div className="main-workspace">
        {/* Barra lateral vertical: navegación entre secciones del bot */}
        <nav
          className={`workspace-nav ${(selectedChat && botSection === 'conversaciones') || (selectedRegistroQuery && botSection === 'registros') ? 'hidden-on-mobile' : ''}`}
          aria-label="Secciones del bot"
        >
          {selectedBotId && botFeatures.chat.has(selectedBotId) && (
          <button
            className={`workspace-nav-item ${botSection === 'conversaciones' ? 'active' : ''}`}
            onClick={() => irA({ botId: selectedBotId, seccion: 'conversaciones' })}
            title="Chat en vivo"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="workspace-nav-label">Chat en vivo</span>
          </button>
          )}
          {selectedBotId && botFeatures.chat.has(selectedBotId) && (
          <button
            className={`workspace-nav-item ${botSection === 'contactos' ? 'active' : ''}`}
            onClick={() => { setContactoInicial(null); setFichaDesdeChat(false); irA({ botId: selectedBotId, seccion: 'contactos' }); }}
            title="Contactos"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="workspace-nav-label">Contactos</span>
          </button>
          )}
          {/* Sección "Atención al cliente": sólo si el usuario tiene permiso de
              agente en el bot seleccionado. */}
          {selectedBotId && atencionAccessBotIds.has(selectedBotId) && (
            <button
              className={`workspace-nav-item ${botSection === 'atencion' ? 'active' : ''}`}
              onClick={() => irA({ botId: selectedBotId, seccion: 'atencion' })}
              title="Atención al cliente"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
                <path d="M4 12a8 8 0 0116 0v5a2 2 0 01-2 2h-2v-6h4M4 12v5a2 2 0 002 2h2v-6H4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="workspace-nav-label">Atención al cliente</span>
            </button>
          )}
          {selectedBotId && botFeatures.registros.has(selectedBotId) && (
          <button
            className={`workspace-nav-item ${botSection === 'registros' ? 'active' : ''}`}
            onClick={() => {
              setRegistroQueries([]);
              setSelectedRegistroQuery(null);
              setRegistroQueryResult(null);
              if (botSection === 'registros') loadRegistroQueries(selectedBotId);
              else irA({ botId: selectedBotId, seccion: 'registros' });
            }}
            title="Registros"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="workspace-nav-label">Registros</span>
          </button>
          )}
          {/* Sección "Instagram": conector + comentarios. Se pinta con el permiso
              de Instagram; quien ve la pestaña ya gestiona comentarios y auto-DM.
              Antes exigía que el bot ya tuviera canal IG, así que el conector no se
              veía hasta haber conectado: un huevo sin gallina. */}
          {selectedBotId && botFeatures.instagram.has(selectedBotId) && (
            <button
              className={`workspace-nav-item ${botSection === 'comentarios-ig' ? 'active' : ''}`}
              onClick={() => irA({ botId: selectedBotId, seccion: 'comentarios-ig' })}
              title="Instagram"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
                <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8"/>
                <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8"/>
                <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor"/>
              </svg>
              <span className="workspace-nav-label">Instagram</span>
            </button>
          )}
          {/* Sección "WhatsApp": configuración y Embedded Signup */}
          {selectedBotId && botFeatures.whatsapp.has(selectedBotId) && (
            <button
              className={`workspace-nav-item ${botSection === 'whatsapp' ? 'active' : ''}`}
              onClick={() => irA({ botId: selectedBotId, seccion: 'whatsapp' })}
              title="Configuración de WhatsApp"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="workspace-nav-label">WhatsApp</span>
            </button>
          )}
          {/* Sección "Plantillas": permiso propio Y número de WhatsApp conectado de
              verdad. No se mira activeChannels: ese Set solo lista canales con
              webhook_url no vacía, y Embedded Signup nunca la escribía, así que un
              número conectado no aparecía ahí. */}
          {selectedBotId
            && botFeatures.templates.has(selectedBotId)
            && botFeatures.whatsappConnected.has(selectedBotId) && (
            <button
              className={`workspace-nav-item ${botSection === 'plantillas' ? 'active' : ''}`}
              onClick={() => irA({ botId: selectedBotId, seccion: 'plantillas' })}
              title="Plantillas de WhatsApp"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
                <path d="M4 4h16v4H4zM4 12h10M4 16h10M4 20h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="16" y="12" width="4" height="8" rx="1" stroke="currentColor" strokeWidth="1.8"/>
              </svg>
              <span className="workspace-nav-label">Plantillas</span>
            </button>
          )}
          {selectedBotId && botFeatures.contrato.has(selectedBotId) && (
          <button
            className={`workspace-nav-item ${botSection === 'contrato' ? 'active' : ''}`}
            onClick={() => irA({ botId: selectedBotId, seccion: 'contrato' })}
            title="Contrato"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
              <path d="M7 3h8l4 4v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
              <path d="M15 3v5h5M8 12h8M8 16h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <span className="workspace-nav-label">Contrato</span>
          </button>
          )}
          {isAdmin && (
            <button
              className={`workspace-nav-item ${botSection === 'configuracion' ? 'active' : ''}`}
              onClick={() => irA({ botId: selectedBotId, seccion: 'configuracion' })}
              title="Configuración"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="workspace-nav-icon">
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="workspace-nav-label">Configuración</span>
            </button>
          )}
        </nav>

      {/* Chat Environment: el sidebar muestra la lista de chats en
          "conversaciones" y la lista de consultas en "registros".
          En "instagram" el sidebar se oculta por completo y el panel
          de Instagram ocupa todo el ancho disponible. */}
      <div className={`chat-environment section-${botSection}`}>
        {/* Chat List Sidebar */}
        <div className={`chat-sidebar ${botSection === 'comentarios-ig' || botSection === 'plantillas' || botSection === 'atencion' || botSection === 'whatsapp' || botSection === 'configuracion' || botSection === 'contrato' || botSection === 'contactos' ? 'hidden-section' : ''} ${selectedChat && botSection === 'conversaciones' ? 'hidden' : ''} ${selectedRegistroQuery && botSection === 'registros' ? 'hidden' : ''}`}>
          
          {botSection === 'conversaciones' && (
            <ChatsPanel
              chatFilters={chatFilters}
              setChatFilters={setChatFilters}
              availableChannels={availableChannels}
              appliedChatFilters={appliedChatFilters}
              chatsPagination={chatsPagination}
              handleSearchChats={handleSearchChats}
              handleClearChatFilters={handleClearChatFilters}
              handleRemoveChatFilter={handleRemoveChatFilter}
              chats={chats}
              selectedChat={selectedChat}
              handleChatSelect={handleChatSelect}
              loadMoreChats={loadMoreChats}
              botTimezonesMap={botTimezonesMap}
            />
          )}

          {botSection === 'registros' && (
            <RegistroQueriesPanel
              view="sidebar"
              isAdmin={isAdmin}
              selectedBotId={selectedBotId}
              selectedRegistroQuery={selectedRegistroQuery}
              registroQueries={registroQueries}
              handleSelectRegistroQuery={handleSelectRegistroQuery}
              onQueriesChanged={(deletedId) => {
                if (selectedBotId) loadRegistroQueries(selectedBotId);
                if (deletedId && selectedRegistroQuery?.id === deletedId) {
                  setSelectedRegistroQuery(null);
                  setRegistroQueryResult(null);
                }
              }}
            />
          )}

          {botSection === 'comentarios-ig' && (
            <div className="registros-empty-sidebar">
              <p>La configuración de Instagram y la gestión de comentarios se realizan en el panel principal.</p>
            </div>
          )}
          {botSection === 'plantillas' && (
            <div className="registros-empty-sidebar">
              <p>La gestión de plantillas de WhatsApp se realiza en el panel principal.</p>
            </div>
          )}
        </div>

        {/* Chat Main Area */}
        <div className={`chat-main ${(selectedChat && botSection === 'conversaciones') || (botSection === 'registros' && selectedRegistroQuery) || botSection === 'comentarios-ig' || botSection === 'plantillas' || botSection === 'atencion' || botSection === 'whatsapp' || botSection === 'configuracion' || botSection === 'contrato' || botSection === 'contactos' ? 'show' : ''}`}>
          {botSection === 'contactos' ? (
            <div className="registros-main-content">
              <ContactosPanel
                key={`${selectedBotId}-${contactoInicial ?? 'lista'}`}
                botId={selectedBotId}
                isAdmin={isAdmin}
                contactoInicial={contactoInicial}
                onVolverAlChat={fichaDesdeChat ? () => {
                  setContactoInicial(null);
                  setFichaDesdeChat(false);
                  irA({ botId: selectedBotId, seccion: 'conversaciones' });
                } : null}
              />
            </div>
          ) : botSection === 'contrato' ? (
            <div className="registros-main-content">
              <LegalContractSection key={selectedBotId} botId={selectedBotId} />
            </div>
          ) : botSection === 'configuracion' ? (
            <div className="registros-main-content configuracion-settings-main">
              <ConfiguracionPanel
                selectedBot={accessibleBots.find(b => Number(b.botId) === Number(selectedBotId))}
                configTab={configTab}
                onConfigTabChange={setConfigTab}
                appliedTaskTimezone={appliedTaskTimezone}
                isAdmin={isAdmin}
                onBotUpdated={({ botId, ...rest }) => {
                  const aplicar = (prev) => prev.map(b =>
                    Number(b.botId) === Number(botId) ? { ...b, ...rest } : b
                  );
                  setAccessibleBots(aplicar);
                  setAdminBots(aplicar);
                  if (rest.timezone) {
                    setAppliedTaskTimezone({ botId, timezone: rest.timezone });
                  }
                }}
              />
            </div>
          ) : botSection === 'whatsapp' ? (
            <div className="registros-main-content whatsapp-settings-main">
              {selectedBotId ? (
                <WhatsAppSettingsPanel
                  key={selectedBotId}
                  botId={selectedBotId}
                  botName={accessibleBots.find(b => b.botId === selectedBotId)?.botName}
                />
              ) : (
                <div className="registros-placeholder">
                  <h2>WhatsApp</h2>
                  <p>Selecciona un bot en la cabecera.</p>
                </div>
              )}
            </div>
          ) : botSection === 'atencion' ? (
            <div className="registros-main-content">
              <SupportInbox botId={selectedBotId} />
            </div>
          ) : botSection === 'plantillas' ? (
            <div className="registros-main-content">
              {selectedBotId ? (
                <WhatsAppTemplatesPanel botId={selectedBotId} zonaHoraria={botTimezonesMap[String(selectedBotId)]} />
              ) : (
                <div className="registros-placeholder">
                  <h2>Plantillas de WhatsApp</h2>
                  <p>Selecciona un bot con WhatsApp en la cabecera.</p>
                </div>
              )}
            </div>
          ) : botSection === 'comentarios-ig' ? (
            <div className="registros-main-content instagram-settings-main">
              {selectedBotId ? (
                <InstagramSettingsPanel
                  botId={selectedBotId}
                />
              ) : (
                <div className="registros-placeholder">
                  <h2>Instagram</h2>
                  <p>Selecciona un bot con Instagram en la cabecera.</p>
                </div>
              )}
            </div>
          ) : botSection === 'registros' ? (
            <RegistroQueriesPanel
              view="main"
              selectedBotId={selectedBotId}
              selectedRegistroQuery={selectedRegistroQuery}
              registroQueries={registroQueries}
              registroQueryResult={registroQueryResult}
              registroQueryLoading={registroQueryLoading}
              executeRegistroQuery={executeRegistroQuery}
            />
          ) : (
            <MessagesPanel
              selectedChat={selectedChat}
              selectedBotId={selectedBotId}
              accessibleBots={accessibleBots}
              supportStatus={supportStatus}
              supportActionLoading={supportActionLoading}
              handleCaptureConversation={handleCaptureConversation}
              handleCloseConversation={handleCloseConversation}
              messagesContainerRef={messagesContainerRef}
              messagesPagination={messagesPagination}
              loadMoreMessages={loadMoreMessages}
              message={message}
              setMessage={setMessage}
              handleKeyPress={handleKeyPress}
              handleSendMessage={handleSendMessage}
              messageInputRef={messageInputRef}
              ventanaCerrada={ventanaCerrada}
              sendErrorText={sendErrorText}
              alCargarMedia={alCargarMedia}
              enviandoMedia={enviandoMedia}
              handleSendMedia={handleSendMedia}
              onVerFicha={(chat) => {
                setContactoInicial(chat.internalChatId);
                setFichaDesdeChat(true);
                irA({ botId: selectedBotId, seccion: 'contactos' });
              }}
            />
          )}
        </div>
      </div>
      </div>
      ) : activeTab === 'administracion' ? (
      /* Administración */
      <div className="admin-environment">
        <AdminPanel
          adminUsers={adminUsers}
          adminBots={adminBots}
          adminLoading={adminLoading}
          adminError={adminError}
          setAdminLoading={setAdminLoading}
          loadAdminData={loadAdminData}
          onBotCreated={loadAccessibleBots}
          onBotDeleted={(botId) => {
            if (Number(selectedBotIdRef.current) === Number(botId)) {
              selectedBotIdRef.current = null;
            }
            loadAccessibleBots();
          }}
        />
      </div>
      ) : null}

      {/* Modal de resultado del flujo OAuth de Instagram */}
      {instagramOAuthResult && createPortal(
        <div className="modal-overlay" onClick={() => setInstagramOAuthResult(null)}>
          <div
            className="modal-content confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ig-oauth-result-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="ig-oauth-result-title">
                {instagramOAuthResult.status === 'success'
                  ? 'Instagram conectado'
                  : 'Error conectando Instagram'}
              </h3>
              <button className="modal-close" onClick={() => setInstagramOAuthResult(null)}>×</button>
            </div>
            <div className="confirm-modal-body">
              {instagramOAuthResult.status === 'success' ? (
                <>
                  <p>
                    La cuenta de Instagram se ha vinculado correctamente
                    {instagramOAuthResult.username
                      ? <> como <strong>@{instagramOAuthResult.username}</strong></>
                      : null}.
                  </p>
                  <p className="confirm-modal-note">
                    Los webhooks de mensajes se han suscrito automáticamente.
                    A partir de ahora, los DMs entrantes aparecerán en el panel.
                  </p>
                  {instagramOAuthResult.botChannelId && (
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      bot_channel_id: <code>{instagramOAuthResult.botChannelId}</code>
                      {instagramOAuthResult.igUserId && (
                        <> · IG user_id: <code>{instagramOAuthResult.igUserId}</code></>
                      )}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>{instagramOAuthResult.message}</p>
                  {instagramOAuthResult.code && instagramOAuthResult.code !== 'unknown' && (
                    <p className="confirm-modal-note">
                      Código de error: <code>{instagramOAuthResult.code}</code>
                    </p>
                  )}
                  <p className="confirm-modal-note">
                    Comprueba que el bot tenga <strong>app_id</strong>, <strong>app_secret</strong> y
                    <strong> oauth_redirect_uri</strong> configurados en la base de datos,
                    y que la URL de callback registrada en el Dashboard de Meta coincida exactamente
                    con la del bot.
                  </p>
                </>
              )}
            </div>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="admin-btn primary"
                onClick={() => setInstagramOAuthResult(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Dashboard;
